import { mapEvaluateResponseToTextMode } from '../api';
import type { FlexiObservation } from '../api';
import type { useNoteStore } from '../stores/note.store';
import type { useQuestionStore } from '../stores/question.store';
import type { usePatientStore } from '../stores/patient.store';
import type { useEvidenceStore } from '../stores/evidence.store';
import type { useGuidedModeStore } from '@/features/guided-mode/stores/guidedMode';
import type { useTrackStore } from '@/stores/track';
import {
  buildReviewObservations, flattenSteps,
} from '@/features/guided-mode/utils/guided-observations';

interface PrepareTextWeightOnStopParams {
  noteStore: ReturnType<typeof useNoteStore>;
  questionStore: ReturnType<typeof useQuestionStore>;
  patientStore: ReturnType<typeof usePatientStore>;
  guidedModeStore: ReturnType<typeof useGuidedModeStore>;
  evidenceStore: ReturnType<typeof useEvidenceStore>;
  trackStore: ReturnType<typeof useTrackStore>;
}

function recordUnknownWeightOnStop({
  noteStore,
  questionStore,
  patientStore,
  guidedModeStore,
  evidenceStore,
  trackStore,
}: PrepareTextWeightOnStopParams): void {
  const weight = noteStore.originalObservations.find(item => item.name === 'weight');
  if (weight && weight.value !== undefined && weight.value !== null
    && String(weight.value).trim() !== '') return;

  const observation = {
    ...weight,
    name: 'weight',
    value: 'Unknown',
    type: 'number',
    category: 'vital_signs',
    source: 'response',
    nameToken: 'questions.weight.title',
  } satisfies FlexiObservation;
  const weightQuestion = questionStore.questions.find(question => question.id === 'weight');
  if (weightQuestion?.type === 'number') observation.numericOptions = weightQuestion.options;

  const patientType = patientStore.patient?.type;
  const definition = patientType
    ? guidedModeStore.clinicalContent.assessmentDefinitions?.[patientType] : undefined;
  const [withMetadata] = buildReviewObservations({
    definition,
    steps: flattenSteps(definition),
    observations: [observation],
  });
  const recordedWeight = withMetadata ?? observation;
  const observations = weight
    ? noteStore.originalObservations.map(item => item === weight ? recordedWeight : item)
    : [...noteStore.originalObservations, recordedWeight];
  noteStore.setOriginalObservations(observations);
  const mapped = mapEvaluateResponseToTextMode({
    observations,
    patient: patientStore.patient ?? undefined,
    questions: [],
    extractions: noteStore.originalExtractions,
    focusedAssessments: noteStore.focusedAssessments,
    comprehensive: trackStore.comprehensive,
    conditions: trackStore.filterConditions,
  });
  evidenceStore.setEvidenceCategories(mapped.evidenceCategories);
}

export function prepareTextWeightOnStop(params: PrepareTextWeightOnStopParams): void {
  recordUnknownWeightOnStop(params);
  params.questionStore.unaskedQuestionNames = params.questionStore.unaskedQuestionNames
    .filter(name => name !== 'weight');
}
